import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import api from '../utils/api';

interface SessionTimeoutContextType {
  resetTimer: () => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

const TIMEOUT_DURATION = 45 * 60 * 1000; // 45 minutes in milliseconds

export const SessionTimeoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: contextUser, setUser } = useUser();

  const resetTimer = useCallback(() => {
    // Don't set timer if we're on the login page
    if (location.pathname === '/login') {
      return;
    }

    // Check if we're still authenticated via user context
    if (!contextUser) {
      return;
    }

    // Clear existing timer
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Set new timeout timer
    timeoutIdRef.current = setTimeout(async () => {
      try {
        // Call logout endpoint to clear the HttpOnly cookie
        await api.post('/api/logout', {}, { withCredentials: true });
      } catch (error) {
        console.error('Error during session timeout logout:', error);
      } finally {
        // Clear local user data
        setUser(null);
        // Redirect to login
        navigate('/login', { state: { from: location }, replace: true });
      }
    }, TIMEOUT_DURATION);
  }, [navigate, location, contextUser, setUser]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    const handleUserActivity = () => {
      resetTimer();
    };

    // Only add listeners if we're authenticated and not on login page
    if (contextUser && location.pathname !== '/login') {
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity);
      });

      // Initial timer setup
      resetTimer();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity);
        });
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
      };
    }
  }, [resetTimer, location.pathname, contextUser]);

  return (
    <SessionTimeoutContext.Provider value={{ resetTimer }}>
      {children}
    </SessionTimeoutContext.Provider>
  );
};

export const useSessionTimeout = () => {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider');
  }
  return context;
};

export default SessionTimeoutProvider; 